import type { HourlyWeatherItem, WeeklyWeatherItem } from "./weatherTypes";

const ROBOT_CLEANER_FORECAST_START_TIME = "1000";
const ROBOT_CLEANER_FORECAST_END_TIME = "1700";
const ROBOT_CLEANER_RAIN_PROB_WARNING = 50;

export interface RobotCleanerWeatherSummary {
  date: string;
  icon: string;
  minTemp: number;
  maxTemp: number;
  maxRainProb: number;
  isCaution: boolean;
  statusText: string;
  cautionText: string;
}

function getRepresentativeHourlyWeatherItem(summaryItems: HourlyWeatherItem[]) {
  return (
    summaryItems.find((item) => item.time === "1200") ||
    summaryItems.find((item) => item.time === "1500") ||
    summaryItems.find((item) => item.time >= "1200") ||
    summaryItems[Math.floor(summaryItems.length / 2)] ||
    summaryItems[0]
  );
}

function getRobotCleanerCautionText(isCaution: boolean) {
  return isCaution
    ? "비 예보가 있어 창문 청소 효율이 떨어질 수 있습니다. 날씨가 맑은 날 예약을 권장합니다."
    : "창문 청소하기 무난한 날씨입니다.";
}

export const getWeatherIcon = (rainType?: string, skyStatus?: string, time: string = "1200") => {
  if (!skyStatus) return "/images/weather/ic-weather-sun.svg";

  const hour = parseInt(time.substring(0, 2), 10);
  const isNight = hour >= 20 || hour < 6;

  if (rainType === "비") return "/images/weather/ic-weather-rainy.svg";
  if (rainType === "비/눈") return "/images/weather/ic-weather-snow.svg";
  if (rainType === "소나기") return "/images/weather/ic-weather-rainy.svg";

  switch (skyStatus) {
    case "맑음":
      return isNight ? "/images/weather/ic-weather-moon.svg" : "/images/weather/ic-weather-sun.svg";
    case "구름많음":
      return isNight ? "/images/weather/ic-weather-night-cloud.svg" : "/images/weather/ic-weather-sunny-cloud.svg";
    case "흐림":
      return "/images/weather/ic-weather-cloudy.svg";
    default:
      return isNight ? "/images/weather/ic-weather-moon.svg" : "/images/weather/ic-weather-sun.svg";
  }
};

export function isRainWeather(rainType?: string) {
  return Boolean(rainType && rainType !== "없음");
}

export function isRobotCleanerCautionWeather(item: HourlyWeatherItem) {
  return isRainWeather(item.rainType) || (item.rainProb ?? 0) >= ROBOT_CLEANER_RAIN_PROB_WARNING;
}

export function getRobotCleanerWeatherHours(date: string, hourlyWeatherInfo: HourlyWeatherItem[] = []) {
  return hourlyWeatherInfo.filter(
    (item) =>
      item.date === date &&
      item.time >= ROBOT_CLEANER_FORECAST_START_TIME &&
      item.time <= ROBOT_CLEANER_FORECAST_END_TIME
  );
}

export function getRobotCleanerWeatherSummary(
  date: string,
  hourlyWeatherInfo: HourlyWeatherItem[] = []
): RobotCleanerWeatherSummary | undefined {
  const dayItems = hourlyWeatherInfo.filter((item) => item.date === date);
  if (dayItems.length === 0) return undefined;

  const useTimeItems = getRobotCleanerWeatherHours(date, hourlyWeatherInfo);
  const summaryItems = useTimeItems.length > 0 ? useTimeItems : dayItems;
  const temps = summaryItems.map((item) => item.temp);
  const rainProbs = summaryItems.map((item) => item.rainProb ?? 0);
  const representative = getRepresentativeHourlyWeatherItem(summaryItems);
  const maxRainProb = Math.max(...rainProbs);
  const isCaution = summaryItems.some(isRobotCleanerCautionWeather);
  const statusText = isRainWeather(representative.rainType)
    ? representative.rainType || "비 예보"
    : representative.skyStatus || "예보";

  return {
    date,
    icon: getWeatherIcon(representative.rainType, representative.skyStatus, representative.time),
    minTemp: Math.min(...temps),
    maxTemp: Math.max(...temps),
    maxRainProb,
    isCaution,
    statusText,
    cautionText: getRobotCleanerCautionText(isCaution),
  };
}

export function getRobotCleanerWeeklyWeatherSummary(
  item: WeeklyWeatherItem
): RobotCleanerWeatherSummary | undefined {
  if (!item.date) return undefined;

  const rainProbAm = item.rainProbAm ?? 0;
  const rainProbPm = item.rainProbPm ?? 0;
  const maxRainProb = Math.max(rainProbAm, rainProbPm);
  const hasRain = isRainWeather(item.rainTypeAm) || isRainWeather(item.rainTypePm);
  const isCaution = hasRain || maxRainProb >= ROBOT_CLEANER_RAIN_PROB_WARNING;
  const representativeRainType = isRainWeather(item.rainTypePm) ? item.rainTypePm : item.rainTypeAm;
  const representativeSky = item.skyPm || item.skyAm;
  const statusText = isRainWeather(representativeRainType)
    ? representativeRainType || "비 예보"
    : representativeSky || "예보";

  return {
    date: item.date,
    icon: getWeatherIcon(representativeRainType, representativeSky, "1500"),
    minTemp: item.minTemp,
    maxTemp: item.maxTemp,
    maxRainProb,
    isCaution,
    statusText,
    cautionText: getRobotCleanerCautionText(isCaution),
  };
}
